import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface RootImpactReferenceDatasetBase {
  name: string;
  geography: string;
  unit: string;
  elementary_eco_invent_dataset_id: number | null;
  note: string;
}

export interface RootImpactReferenceDataset extends RootImpactReferenceDatasetBase {
  id: number;
}

export interface RootImpactReferenceExchange {
  id: number;
  dataset: RootImpactReferenceDataset;
  amount: number;
  not_absolute: boolean;
}
export interface RootImpactReferenceElementaryExchange {
  name: string;
  unit: string;
  amount: number;
}

export interface GetRootImpactReference {
  dataset: RootImpactReferenceDataset;
  exchanges: RootImpactReferenceExchange[];
  elementary_exchanges: RootImpactReferenceElementaryExchange[];
}

export function useGetRootImpactReference({
  id,
  fromEcoInventDataset,
}: {
  id?: number;
  fromEcoInventDataset?: boolean;
}) {
  return useQuery({
    queryKey: ["root_impact_reference", id],
    keepPreviousData: true,
    enabled: !!id,
    queryFn: () =>
      axios
        .get<GetRootImpactReference>(
          "/impact_references/root/" +
            id +
            "?from_eco_invent_dataset=" +
            Boolean(fromEcoInventDataset)
        )
        .then((result) => result.data),
  });
}

export interface PostExchange {
  id: number;
  amount: number;
}

export interface PostRootImpactReference {
  dataset: RootImpactReferenceDatasetBase;
  exchanges: PostExchange[];
}

export function useInsertRootImpactReference() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ post }: { post: PostRootImpactReference }) =>
      axios.post("/impact_references/root", post),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}

export function useUpdateRootImpactReference() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ id, post }: { id: number; post: PostRootImpactReference }) =>
      axios.post("/impact_references/root/" + id, post),
    onSuccess: (response) => {
      smartQueryClient.invalidateQueries(["root_impact_reference", response.data.id]);
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}

export function useDeleteRootImpactReference() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id?: number }) => axios.delete("/impact_references/root/" + id),
    onSuccess: (response) => {
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}
